import { memo, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Topbar from "../components/Topbar";
import Footer from "../components/Footer";
import { useContentContext, useAuthContext } from "../../app/providers/contextIndex";
import { ensureCatalogAuth, getAffariCanal } from "../../services/backend/authService";
import { getFamilias } from "../../services/backend/catalogService";
import ContentService from "../../features/content/utils/contentService";
import { normalizeSlug } from "../../features/content/utils/pageConfig";
import { useContentVersion } from "../../features/content/hooks/useContentVersion";
import { buildTopbarFamiliesConfig } from "../utils/familyMenu";
import { getCompany } from "../../config/companyConfig";
import "../styles/landing-layout.css";

const LandingLayout = memo(() => {
  const COMPANY = getCompany();
  const { getPagesByLanding } = useContentContext();
  const { appCanal } = useAuthContext();
  const activeLandingId = COMPANY.id;
  const [dynamicFamilies, setDynamicFamilies] = useState(null);
  const location = useLocation();

  useEffect(() => {
    ContentService.refreshContenidosCache();
  }, [location.pathname]);

  const { primaryColor, favicon, topbar: companyTopbar, footer: companyFooter, floatingWhatsapp } = COMPANY;

  useEffect(() => {
    const root = document.documentElement;
    const color = primaryColor || "#1A4692";
    root.style.setProperty("--company-primary", color);
    root.style.setProperty("--dc-accent", color);
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    root.style.setProperty("--company-primary-rgb", `${r}, ${g}, ${b}`);
    if (favicon) {
      const link = document.querySelector("link[rel~='icon']");
      if (link) link.href = favicon;
      try { sessionStorage.setItem("appCompanyFavicon", favicon); } catch {}
    }
  }, [primaryColor, favicon]);

  const topbarConfig = useMemo(() => {
    if (!dynamicFamilies) return companyTopbar || {};
    return { ...(companyTopbar || {}), ...dynamicFamilies };
  }, [dynamicFamilies, companyTopbar]);

  const customPages = useMemo(
    () => getPagesByLanding(activeLandingId),
    [getPagesByLanding, activeLandingId]
  );
  const contentVersion = useContentVersion();
  const jsonPages = useMemo(() => {
    return ContentService.getPagesForLanding(activeLandingId);
  }, [activeLandingId, contentVersion]); // eslint-disable-line react-hooks/exhaustive-deps
  const footerPages = useMemo(() => {
    const merged = [];
    const slugs = new Set();
    const labels = new Set();
    [...jsonPages, ...customPages].forEach((page) => {
      const slug = normalizeSlug(page?.slug);
      const label = normalizeSlug(page?.navLabel || page?.title || "");
      if (!slug || slugs.has(slug) || (label && labels.has(label))) {
        return;
      }
      slugs.add(slug);
      if (label) {
        labels.add(label);
      }
      merged.push(page);
    });
    return merged;
  }, [customPages, jsonPages]);
  const footerLinksByColumn = useMemo(() => {
    return footerPages.reduce((acc, page) => {
      const targetColumn = Number.isInteger(page?.footerColumn) ? page.footerColumn : 0;
      const link = {
        label: page?.navLabel || page?.title || page?.slug,
        to: page?.textOnly ? null : page?.slug,
        textOnly: page?.textOnly === true,
      };
      const currentLinks = Array.isArray(acc[targetColumn]) ? acc[targetColumn] : [];
      acc[targetColumn] = [...currentLinks, link];
      return acc;
    }, {});
  }, [footerPages]);
  const footerConfig = useMemo(() => {
    const footer = companyFooter || {};
    const columns = Array.isArray(footer.columns) ? footer.columns : [];
    const linkIndexes = Object.keys(footerLinksByColumn)
      .map((key) => Number(key))
      .filter((value) => Number.isInteger(value) && value >= 0);
    if (!columns.length && !linkIndexes.length) {
      return footer;
    }
    const maxIndex = linkIndexes.length
      ? Math.max(columns.length - 1, ...linkIndexes)
      : columns.length - 1;
    const normalizedColumns = Array.from({ length: maxIndex + 1 }, (_, index) => (
      columns[index] ? columns[index] : { title: `Columna ${index + 1}`, links: [] }
    ));
    const updatedColumns = normalizedColumns.map((column, index) => (
      Array.isArray(footerLinksByColumn[index]) && footerLinksByColumn[index].length
        ? { ...column, links: footerLinksByColumn[index] }
        : column
    ));
    return { ...footer, columns: updatedColumns, canal: getAffariCanal() };
  }, [footerLinksByColumn, companyFooter]);

  useEffect(() => {
    let isCurrent = true;

    const loadFamilies = async () => {
      try {
        await ensureCatalogAuth();
        let response = await getFamilias({ tipo: appCanal });
        let nextFamilies = buildTopbarFamiliesConfig(response);

        if (!isCurrent) {
          return;
        }
        setDynamicFamilies(nextFamilies.catalogMenu.length ? nextFamilies : null);
      } catch {
        if (isCurrent) {
          setDynamicFamilies(null);
        }
      }
    };

    loadFamilies();

    return () => {
      isCurrent = false;
    };
  }, [location.pathname, appCanal]);

  return (
    <div className={`landing-shell landing-theme-${activeLandingId}`}>
      <Topbar config={topbarConfig} />
      <main className="landing-main">
        <Outlet context={{ dynamicFamilies, activeLandingId }} />
      </main>
      <Footer config={footerConfig} />
      <a
        href={floatingWhatsapp?.href}
        target="_blank"
        rel="noreferrer noopener"
        className="float-wa"
        aria-label={floatingWhatsapp?.ariaLabel}
        title={floatingWhatsapp?.title}
      >
        <i className={floatingWhatsapp?.icon}></i>
      </a>
    </div>
  );
});

export default LandingLayout;
